import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient, ApiError } from '../lib/api';
import type { AdminCapabilities, AdminRoleDto, AdminUserDto, CreateUserRequest } from '../lib/types';
import { useIsAdmin } from '../hooks/useIsAdmin';
import { useCurrentUser } from '../hooks/useCurrentUser';

export function AdminUsers() {
  const isAdmin = useIsAdmin();
  const { data: me } = useCurrentUser();

  if (!isAdmin) {
    return (
      <div className="bg-white rounded-lg shadow border border-slate-200 p-6">
        <h1 className="text-xl font-bold text-atg-navy">Forbidden</h1>
        <p className="text-slate-600 mt-2">You need the <code>Admin</code> role to view this page.</p>
      </div>
    );
  }

  return <AdminUsersInner currentEmail={me?.email ?? null} />;
}

function AdminUsersInner({ currentEmail }: { currentEmail: string | null }) {
  const qc = useQueryClient();
  const [showCreate, setShowCreate] = useState(false);

  const capsQ = useQuery({
    queryKey: ['admin', 'capabilities'],
    queryFn: () => apiClient.get<AdminCapabilities>('/api/admin/users/capabilities')
  });
  // In IdP mode (Okta / Entra) the page is a pointer to the IdP, not a management surface —
  // don't fetch the local assignment data at all.
  const localAccounts = capsQ.data?.supportsLocalAccounts === true;
  const usersQ = useQuery({
    queryKey: ['admin', 'users'],
    queryFn: () => apiClient.get<AdminUserDto[]>('/api/admin/users'),
    enabled: localAccounts
  });
  const rolesQ = useQuery({
    queryKey: ['admin', 'roles'],
    queryFn: () => apiClient.get<AdminRoleDto[]>('/api/admin/roles'),
    enabled: localAccounts
  });

  const invalidate = () => qc.invalidateQueries({ queryKey: ['admin'] });

  const setRoles = useMutation({
    mutationFn: ({ id, roles }: { id: string; roles: string[] }) =>
      apiClient.put<AdminUserDto>(`/api/admin/users/${id}/roles`, { roles }),
    onSuccess: invalidate
  });
  const toggleDisabled = useMutation({
    mutationFn: ({ id, disabled }: { id: string; disabled: boolean }) =>
      apiClient.put<AdminUserDto>(`/api/admin/users/${id}`, { disabled }),
    onSuccess: invalidate
  });
  const deleteUser = useMutation({
    mutationFn: (id: string) => apiClient.del<void>(`/api/admin/users/${id}`),
    onSuccess: invalidate
  });
  const resetPassword = useMutation({
    mutationFn: ({ id, newPassword }: { id: string; newPassword: string }) =>
      apiClient.post<void>(`/api/admin/users/${id}/reset-password`, { newPassword }),
    onSuccess: invalidate
  });

  const supportsLocalAccounts = localAccounts;
  const knownRoles = (rolesQ.data ?? []).map(r => r.name);
  const fallbackRoles = ['Admin', 'User'];
  const allRoles = knownRoles.length > 0 ? knownRoles : fallbackRoles;

  if (capsQ.isLoading) {
    return <div className="text-slate-500">Loading…</div>;
  }

  // IdP mode: users and roles live in the identity provider, so local edits here would have no
  // effect on authorization. Point at the right place instead of showing a dead management UI.
  if (!supportsLocalAccounts) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-atg-navy">User administration</h1>
          <p className="text-slate-600 mt-1 text-sm">This app authenticates against your identity provider.</p>
        </div>
        <div className="bg-white rounded-lg shadow border border-slate-200 p-6 space-y-3">
          <h2 className="text-lg font-semibold text-atg-navy">Users and roles are managed in your identity provider</h2>
          <p className="text-sm text-slate-600">
            Assign people to the groups mapped to the Admin and User roles
            (<code>Okta:AdminGroup</code> / <code>Okta:UserGroup</code> in <code>appsettings</code>).
            Changes take effect the next time they sign in.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <div>
          <h1 className="text-2xl font-bold text-atg-navy">User administration</h1>
          <p className="text-slate-600 mt-1 text-sm">Identity mode: full user management is available.</p>
        </div>
        <button
          onClick={() => setShowCreate(true)}
          className="ml-auto rounded-md bg-atg-navy text-white px-4 py-2 text-sm font-medium hover:bg-atg-blue"
        >
          New user
        </button>
      </div>

      {usersQ.isLoading && <div className="text-slate-500">Loading users...</div>}
      {usersQ.error && <div className="text-red-600 text-sm">Failed to load users.</div>}

      {usersQ.data && (
        <div className="bg-white rounded-lg shadow border border-slate-200 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-100 text-left">
              <tr>
                <th className="px-4 py-2">Email</th>
                <th className="px-4 py-2">Roles</th>
                <th className="px-4 py-2">Source</th>
                <th className="px-4 py-2">Status</th>
                <th className="px-4 py-2 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {usersQ.data.map(u => {
                const isSelf = !!currentEmail && u.email?.toLowerCase() === currentEmail.toLowerCase();
                return (
                  <tr key={u.id} className="border-t border-slate-200 align-top">
                    <td className="px-4 py-3">
                      <div className="font-medium">{u.email}</div>
                      {isSelf && <div className="text-xs text-atg-blue">(you)</div>}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex flex-wrap gap-2">
                        {allRoles.map(role => {
                          const has = u.roles.some(r => r.toLowerCase() === role.toLowerCase());
                          const disableSelfDemote = isSelf && role.toLowerCase() === 'admin' && has;
                          return (
                            <label key={role} className={`inline-flex items-center gap-1 text-xs px-2 py-1 rounded border ${has ? 'bg-atg-navy text-white border-atg-navy' : 'bg-slate-50 text-slate-700 border-slate-300'} ${disableSelfDemote ? 'opacity-50' : 'cursor-pointer'}`}>
                              <input
                                type="checkbox"
                                checked={has}
                                disabled={disableSelfDemote || setRoles.isPending}
                                onChange={() => {
                                  const next = has
                                    ? u.roles.filter(r => r.toLowerCase() !== role.toLowerCase())
                                    : [...u.roles, role];
                                  setRoles.mutate({ id: u.id, roles: next });
                                }}
                                className="hidden"
                              />
                              {role}
                            </label>
                          );
                        })}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-slate-600">{u.source}</td>
                    <td className="px-4 py-3">
                      {u.disabled
                        ? <span className="text-xs px-2 py-1 rounded bg-red-100 text-red-700">Disabled</span>
                        : <span className="text-xs px-2 py-1 rounded bg-emerald-100 text-emerald-700">Active</span>}
                    </td>
                    <td className="px-4 py-3 text-right whitespace-nowrap">
                      {supportsLocalAccounts && !isSelf && (
                        <button
                          onClick={() => {
                            const np = prompt(`New password for ${u.email}?`);
                            if (np) resetPassword.mutate({ id: u.id, newPassword: np });
                          }}
                          className="text-xs text-atg-blue hover:underline mr-3"
                        >
                          Reset password
                        </button>
                      )}
                      <button
                        disabled={isSelf || toggleDisabled.isPending}
                        onClick={() => toggleDisabled.mutate({ id: u.id, disabled: !u.disabled })}
                        className="text-xs text-slate-700 hover:underline mr-3 disabled:opacity-50"
                      >
                        {u.disabled ? 'Enable' : 'Disable'}
                      </button>
                      <button
                        disabled={isSelf || deleteUser.isPending}
                        onClick={() => {
                          if (confirm(`Delete ${u.email}? This cannot be undone.`)) deleteUser.mutate(u.id);
                        }}
                        className="text-xs text-red-600 hover:underline disabled:opacity-50"
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                );
              })}
              {usersQ.data.length === 0 && (
                <tr><td colSpan={5} className="px-4 py-6 text-center text-slate-500">No users yet.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {(setRoles.error || toggleDisabled.error || deleteUser.error || resetPassword.error) && (
        <div className="text-red-600 text-sm">
          {((setRoles.error || toggleDisabled.error || deleteUser.error || resetPassword.error) as ApiError | undefined)?.message
            ?? 'Action failed.'}
        </div>
      )}

      {showCreate && (
        <CreateUserDialog
          allRoles={allRoles}
          supportsLocalAccounts={supportsLocalAccounts}
          onClose={() => setShowCreate(false)}
          onCreated={() => { setShowCreate(false); invalidate(); }}
        />
      )}
    </div>
  );
}

function CreateUserDialog(props: {
  allRoles: string[];
  supportsLocalAccounts: boolean;
  onClose: () => void;
  onCreated: () => void;
}) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [roles, setRoles] = useState<string[]>(['User']);
  const [err, setErr] = useState<string | null>(null);

  const create = useMutation({
    mutationFn: () => {
      const body: CreateUserRequest = {
        email,
        password: props.supportsLocalAccounts ? password : '',
        roles
      };
      return apiClient.post<AdminUserDto>('/api/admin/users', body);
    },
    onSuccess: props.onCreated,
    onError: (e: unknown) => setErr(e instanceof ApiError ? e.message : 'Create failed')
  });

  return (
    <div className="fixed inset-0 bg-slate-900/40 z-50 flex items-center justify-center" onClick={props.onClose}>
      <div className="bg-white rounded-lg shadow-lg p-6 w-full max-w-md" onClick={e => e.stopPropagation()}>
        <h2 className="text-lg font-bold text-atg-navy mb-4">
          {props.supportsLocalAccounts ? 'Create user' : 'Grant role to email'}
        </h2>
        <form
          onSubmit={e => { e.preventDefault(); setErr(null); create.mutate(); }}
          className="space-y-4"
        >
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Email</label>
            <input
              type="email" required value={email} onChange={e => setEmail(e.target.value)}
              className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-atg-blue"
            />
          </div>
          {props.supportsLocalAccounts && (
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Initial password</label>
              <input
                type="password" required value={password} onChange={e => setPassword(e.target.value)}
                className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-atg-blue"
              />
            </div>
          )}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Roles</label>
            <div className="flex flex-wrap gap-2">
              {props.allRoles.map(r => {
                const has = roles.includes(r);
                return (
                  <label key={r} className={`inline-flex items-center gap-1 text-xs px-2 py-1 rounded border cursor-pointer ${has ? 'bg-atg-navy text-white border-atg-navy' : 'bg-slate-50 text-slate-700 border-slate-300'}`}>
                    <input
                      type="checkbox" checked={has} className="hidden"
                      onChange={() => setRoles(has ? roles.filter(x => x !== r) : [...roles, r])}
                    />
                    {r}
                  </label>
                );
              })}
            </div>
          </div>
          {err && <div className="text-red-600 text-sm">{err}</div>}
          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={props.onClose} className="px-3 py-2 text-sm text-slate-700 hover:underline">Cancel</button>
            <button
              type="submit" disabled={create.isPending}
              className="rounded-md bg-atg-navy text-white px-4 py-2 text-sm font-medium hover:bg-atg-blue disabled:opacity-50"
            >
              {create.isPending ? 'Saving...' : 'Save'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
